import React, { useMemo, useState } from "react";
import AppLayout from "../components/AppLayout";
import Header from "../components/header";
import {
  Button,
  IconButton,
  MenuItem,
  Paper,
  Card,
  CardContent,
  Grid,
  Box,
  Typography,
  Tabs,
  Tab,
  TextField,
  Stack,
  FormControl,
  InputLabel,
  Select,
} from "@mui/material";
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import dayjs from "dayjs";
import {
  MdDownload,
  MdPeople,
  MdRefresh,
  MdAttachMoney,
  MdEvent,
  MdTrendingUp,
  MdFilterList,
} from "react-icons/md";
import { motion } from "framer-motion";
import { useSelector } from "react-redux";
import { getBusinessStaff } from "../logic/business/action";
import { useDispatch } from "react-redux";
import { getAppointmentReport, getPaitientsReport, getRevenueReport } from "../logic/reports/action";
import moment from "moment";
import { AgGridReact } from 'ag-grid-react';
import 'ag-grid-community/styles/ag-grid.css';
import 'ag-grid-community/styles/ag-theme-alpine.css';
const StatCard = ({ title, value, icon: Icon, color, trend }) => {
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3 }}
    >
      <Card
        elevation={0}
        sx={{
          borderRadius: '12px',
          background: `linear-gradient(135deg, ${color}10 0%, ${color}05 100%)`,
          border: `1px solid ${color}20`,
          transition: 'all 0.2s ease',
          '&:hover': {
            transform: 'translateY(-2px)',
            boxShadow: 2,
          },
        }}
      >
        <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Box sx={{ flex: 1 }}>
              <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5, fontWeight: 600, fontSize: '0.75rem' }}>
                {title}
              </Typography>
              <Typography variant="h6" sx={{ fontWeight: 700, color: color, mb: 0.25, fontSize: '1.25rem' }}>
                {value}
              </Typography>
              {trend && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.5 }}>
                  <MdTrendingUp size={14} color={color} />
                  <Typography variant="caption" sx={{ color: color, fontWeight: 500, fontSize: '0.7rem' }}>
                    {trend}
                  </Typography>
                </Box>
              )}
            </Box>
            <Box
              sx={{
                width: 40,
                height: 40,
                borderRadius: '10px',
                backgroundColor: `${color}20`,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
                ml: 1,
              }}
            >
              <Icon size={20} color={color} />
            </Box>
          </Box>
        </CardContent>
      </Card>
    </motion.div>
  );
};

const ReportScreen = () => {
  const [staff, setStaff] = useState(0);
  const auth = useSelector((state) => state.auth);
  const buisness = useSelector((state) => state.business);
  const report = useSelector((state) => state.reports);
  const dispatch = useDispatch();
  const [startdate, setStartdate] = useState(dayjs().subtract(1, 'month'));
  const [enddate, setEndDate] = useState(dayjs());
  const [value, setValue] = useState(0);
  const [loading, setLoading] = useState(false);

  const handleChange = (event, newValue) => {
    setValue(newValue);
  };

  const handleLoadReport = () => {
    setLoading(true);
    getAppointmentReport(dispatch, startdate.format("YYYY-MM-DD"), enddate.format("YYYY-MM-DD"), staff);
    getRevenueReport(dispatch, startdate.format("YYYY-MM-DD"), enddate.format("YYYY-MM-DD"), staff);
    getPaitientsReport(dispatch, startdate.format("YYYY-MM-DD"), enddate.format("YYYY-MM-DD"), staff);
    setTimeout(() => setLoading(false), 500);
  };

  React.useEffect(() => {
    getBusinessStaff(dispatch, auth?.user?.business_id);
    getAppointmentReport(dispatch, moment().subtract(1, 'month').format("YYYY-MM-DD"), moment().format("YYYY-MM-DD"), 0);
    getRevenueReport(dispatch, moment().subtract(1, 'month').format("YYYY-MM-DD"), moment().format("YYYY-MM-DD"), 0);
    getPaitientsReport(dispatch, moment().subtract(1, 'month').format("YYYY-MM-DD"), moment().format("YYYY-MM-DD"), 0);
  }, [auth?.user?.business_id]);

  const totalRevenue = report.revenuereport.reduce((total, appointment) => {
    return total + (appointment.price || 0);
  }, 0);

  const handleExport = () => {
    // Export functionality can be added here
    console.log('Exporting report...');
  };
  return (
    <AppLayout>
      <Box sx={{ 
        width: '100%', 
        height: '100%', 
        display: 'flex', 
        flexDirection: 'column', 
        overflow: 'hidden',
        px: 3,
        pt: 1,
        pb: 3, 
        gap: 1.5 
      }}>
        <Header
          title={"Reports"}
          middle={
            <IconButton
              onClick={handleLoadReport}
              sx={{
                bgcolor: 'rgba(102, 126, 234, 0.1)',
                color: '#667eea',
                '&:hover': {
                  bgcolor: 'rgba(102, 126, 234, 0.2)',
                },
              }}
            >
              <MdRefresh />
            </IconButton>
          }
          actions={
            <Button
              variant="contained"
              startIcon={<MdDownload />}
              onClick={handleExport}
              sx={{
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                borderRadius: '12px',
                textTransform: 'none',
                '&:hover': {
                  background: 'linear-gradient(135deg, #764ba2 0%, #667eea 100%)',
                },
              }}
            >
              Export
            </Button>
          }
        />

        {/* Stats Cards */}
        <Grid container spacing={1.5}>
          <Grid item xs={12} sm={6} md={4}>
            <StatCard
              title="Total Revenue"
              value={`₹${totalRevenue.toLocaleString('en-IN')}`}
              icon={MdAttachMoney}
              color="#4caf50"
              trend="+12% from last month"
            />
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <StatCard
              title="Total Appointments"
              value={report.appointmentreport.length}
              icon={MdEvent}
              color="#ff9800"
              trend={`${report.appointmentreport.length} appointments`}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <StatCard
              title="Total Patients"
              value={report.patientreport?.length || report.customerreport?.length || 0}
              icon={MdPeople}
              color="#2196f3"
              trend={`${report.patientreport?.length || report.customerreport?.length || 0} patients`}
            />
          </Grid>
        </Grid>

        {/* Tabs Section with Filters */}
        <Paper
          elevation={0}
          sx={{
            width: '100%',
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            borderRadius: '16px',
            border: '1px solid #e5e7eb',
            backgroundColor: '#ffffff',
            overflow: 'hidden',
            minHeight: 0,
          }}
        >
          {/* Filters Bar */}
          <Box sx={{ 
            p: 2, 
            borderBottom: '1px solid #e5e7eb', 
            backgroundColor: '#f9fafb' 
          }}>
            <Stack 
              direction={{ xs: 'column', md: 'row' }} 
              spacing={2} 
              alignItems={{ xs: 'stretch', md: 'center' }}
            >
              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <DatePicker
                  label="Start Date"
                  value={startdate}
                  onChange={(newValue) => setStartdate(newValue)}
                  format="DD-MM-YYYY"
                  slotProps={{
                    textField: {
                      size: 'small',
                      sx: {
                        '& .MuiOutlinedInput-root': {
                          borderRadius: '8px',
                          backgroundColor: '#ffffff',
                        },
                      },
                    },
                  }}
                />
              </LocalizationProvider>

              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <DatePicker
                  label="End Date"
                  value={enddate}
                  onChange={(newValue) => setEndDate(newValue)}
                  minDate={startdate}
                  format="DD-MM-YYYY"
                  slotProps={{
                    textField: {
                      size: 'small',
                      sx: {
                        '& .MuiOutlinedInput-root': {
                          borderRadius: '8px',
                          backgroundColor: '#ffffff',
                        },
                      },
                    },
                  }}
                />
              </LocalizationProvider>

              <FormControl size="small" sx={{ minWidth: 150 }}>
                <InputLabel>Staff</InputLabel>
                <Select
                  value={staff}
                  label="Staff"
                  onChange={(event) => setStaff(event.target.value)}
                  sx={{
                    borderRadius: '8px',
                    backgroundColor: '#ffffff',
                  }}
                >
                  <MenuItem value={0}>All Staff</MenuItem>
                  {buisness?.staff?.map((e, i) => (
                    <MenuItem key={e.staff_id} value={e.staff_id}>
                      {e?.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <Button
                variant="contained"
                onClick={handleLoadReport}
                disabled={loading}
                sx={{
                  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                  borderRadius: '8px',
                  px: 3,
                  py: 1,
                  textTransform: 'none',
                  fontSize: '0.875rem',
                  fontWeight: 600,
                  whiteSpace: 'nowrap',
                  '&:hover': {
                    background: 'linear-gradient(135deg, #5568d3 0%, #6a3d8f 100%)',
                  },
                }}
              >
                {loading ? 'Loading...' : 'Load Report'}
              </Button>
            </Stack>
          </Box>
          <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
            <Tabs
              value={value}
              onChange={handleChange}
              aria-label="report tabs"
              sx={{
                px: 2,
                '& .MuiTab-root': {
                  textTransform: 'none',
                  fontWeight: 600,
                  fontSize: '0.875rem',
                  minHeight: 48,
                  '&.Mui-selected': {
                    color: '#667eea',
                  },
                },
                '& .MuiTabs-indicator': {
                  backgroundColor: '#667eea',
                  height: 3,
                  borderRadius: '3px 3px 0 0',
                },
              }}
            >
              <Tab
                icon={<MdEvent style={{ marginRight: 8 }} />}
                iconPosition="start"
                label="Appointment Reports"
              />
              <Tab
                icon={<MdPeople style={{ marginRight: 8 }} />}
                iconPosition="start"
                label="Patients Reports"
              />
              <Tab
                icon={<MdAttachMoney style={{ marginRight: 8 }} />}
                iconPosition="start"
                label="Revenue Reports"
              />
            </Tabs>
          </Box>
          
          <Box sx={{ 
            flex: 1, 
            overflow: 'hidden', 
            display: 'flex', 
            flexDirection: 'column',
            minHeight: 0,
            p: 2 
          }}>
            <Box 
              className="ag-theme-alpine" 
              sx={{ 
                flex: 1,
                width: '100%',
                minHeight: 0,
                '& .ag-root-wrapper': {
                  height: '100%',
                },
              }}
            >
            <AgGridReact
              rowData={
                value === 0 ? report.appointmentreport :
                value === 1 ? (report.patientreport || report.customerreport) :
                report.revenuereport
              }
              theme={"legacy"}
              columnDefs={useMemo(() => {
                if (value === 0) {
                  return [
                    {
                      field: 'full_name',
                        headerName: 'Patient Name',
                      flex: 1,
                      minWidth: 150,
                      cellRenderer: (params) => (
                          <Typography
                            sx={{
                              fontWeight: 600,
                              color: '#667eea',
                              textDecoration: 'underline',
                              cursor: 'pointer',
                              '&:hover': {
                                color: '#764ba2',
                              },
                            }}
                          onClick={() => {
                            window.location.href = `/admin/customer/hsitory?customerid=${params.data.patient_id}`;
                          }}
                        >
                          {params.value}
                          </Typography>
                      )
                    },
                    {
                      field: 'start_date',
                      headerName: 'Date',
                      flex: 1,
                      minWidth: 150,
                      valueFormatter: (params) => moment(params.value).format("DD-MM-YYYY")
                    },
                    {
                      field: 'start_time',
                      headerName: 'Time',
                      flex: 1,
                      minWidth: 150,
                      valueFormatter: (params) => moment(params.value, "HH:mm:ss").format("hh:mm A")
                    },
                    {
                      field: 'price',
                      headerName: 'Amount',
                      flex: 1,
                      minWidth: 120,
                        valueFormatter: (params) => `₹${params.value || 0}`
                    }
                  ];
                } else if (value === 1) {
                  return [
                    {
                      field: 'full_name',
                        headerName: 'Patient Name',
                      flex: 1,
                      minWidth: 150,
                      cellRenderer: (params) => (
                          <Typography
                            sx={{
                              fontWeight: 600,
                              color: '#667eea',
                              textDecoration: 'underline',
                              cursor: 'pointer',
                              '&:hover': {
                                color: '#764ba2',
                              },
                            }}
                          onClick={() => {
                            window.location.href = `/admin/customer/hsitory?customerid=${params.data.patient_id}`;
                          }}
                        >
                          {params.value}
                          </Typography>
                      )
                    },
                    {
                      field: 'contact',
                      headerName: 'Contact',
                      flex: 1,
                      minWidth: 150
                    },
                    {
                      field: 'joined_date',
                        headerName: 'Check-in Date',
                      flex: 1,
                      minWidth: 150,
                      valueFormatter: (params) => moment(params.value).format("DD-MM-YYYY")
                    }
                  ];
                } else {
                  return [
                    {
                      field: 'full_name',
                        headerName: 'Patient Name',
                      flex: 1,
                      minWidth: 150,
                      cellRenderer: (params) => (
                          <Typography
                            sx={{
                              fontWeight: 600,
                              color: '#667eea',
                              textDecoration: 'underline',
                              cursor: 'pointer',
                              '&:hover': {
                                color: '#764ba2',
                              },
                            }}
                          onClick={() => {
                            window.location.href = `/admin/customer/hsitory?customerid=${params.data.patient_id}`;
                          }}
                        >
                          {params.value}
                          </Typography>
                      )
                    },
                    {
                      field: 'start_date',
                        headerName: 'Bill Date',
                      flex: 1,
                      minWidth: 150,
                      valueFormatter: (params) => moment(params.value).format("DD-MM-YYYY")
                    },
                    {
                      field: 'start_time',
                        headerName: 'Bill Time',
                      flex: 1,
                      minWidth: 150,
                      valueFormatter: (params) => moment(params.value, "HH:mm:ss").format("hh:mm A")
                    },
                    {
                      field: 'price',
                      headerName: 'Amount',
                      flex: 1,
                      minWidth: 120,
                        valueFormatter: (params) => `₹${params.value || 0}`
                    }
                  ];
                }
              }, [value])}
              defaultColDef={useMemo(() => ({
                sortable: true,
                filter: true,
                resizable: true
              }), [])}
              pagination={true}
                paginationPageSize={10}
              animateRows={true}
            />
            </Box>
          </Box>
        </Paper>
      </Box>
    </AppLayout>
  );
};

export default ReportScreen;
